import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalSidebar from '../components/PortalSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STAGES = [
  'LEAD', 'AGENT_ASSIGNED', 'DOCUMENT_COLLECTION', 'INITIAL_FEE_PAID', 'SUBMITTED',
  'OFFER_RECEIVED', 'DEPOSIT_PAID', 'OFFICIAL_ACCEPTANCE', 'VISA_APPLICATION', 'CLOSED',
];

const stageProgress = (stage) => {
  const idx = STAGES.indexOf(stage);
  return idx === -1 ? 0 : Math.round((idx / (STAGES.length - 1)) * 100);
};

const stageLabel = (stage) => stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function PortalDashboard() {
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE}/auth/me`, { headers }).then((r) => r.json()),
      fetch(`${API_BASE}/portal/applications`, { headers }).then((r) => r.json()),
    ])
      .then(([userJson, appsJson]) => {
        setUser(userJson.success ? userJson.data : null);
        const apps = appsJson.success ? appsJson.data : [];
        setApplications(apps);
        // Pending documents pulled from applications' document checklists
        const pending = apps.flatMap((a) =>
          (a.documents || []).filter((d) => d.status === 'PENDING').map((d) => ({ ...d, applicationId: a.id, applicationTitle: a.opportunity?.title }))
        );
        setDocuments(pending);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeCount = applications.filter((a) => a.currentStage !== 'CLOSED').length;
  const pendingDocsCount = documents.length;
  const inProgress = applications.find((a) => a.currentStage !== 'CLOSED' && a.currentStage !== 'LEAD');

  if (loading) return <p className="text-center py-40 text-[#64748b]">Loading dashboard...</p>;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <PortalSidebar />
      <main className="md:ml-64 min-h-screen relative">
        <header className="fixed top-0 right-0 left-0 md:left-64 z-30 bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-sm h-20">
          <div className="flex justify-between items-center h-full px-6 max-w-[1280px] mx-auto">
            <h2 className="text-xl font-semibold text-[#00255a]">Dashboard</h2>
            <div className="flex items-center gap-6">
              <Link to="/portal/notifications" className="relative p-2 text-[#44474f] hover:text-[#00255a]">
                <span className="material-symbols-outlined">notifications</span>
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#ff7b7b] rounded-full border-2 border-white" />
              </Link>
              <div className="flex items-center gap-3 pl-4 border-l border-[#e2e8f0]">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-semibold text-[#0f172a]">{user?.fullName}</p>
                  <p className="text-xs text-[#64748b]">{user?.email}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#8fb3ff] flex items-center justify-center text-white font-bold">
                  {user?.fullName?.charAt(0) || '?'}
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="pt-24 pb-12 px-6 max-w-[1280px] mx-auto">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Welcome back, {user?.fullName?.split(' ')[0]}</h2>
            <p className="text-[#64748b]">
              You have {activeCount} active application{activeCount !== 1 ? 's' : ''} and {pendingDocsCount} document{pendingDocsCount !== 1 ? 's' : ''} pending.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-[#00255a]/5 rounded-xl text-[#00255a]">
                  <span className="material-symbols-outlined">description</span>
                </div>
              </div>
              <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Active Applications</p>
              <h3 className="text-3xl font-bold text-[#0f172a]">{String(activeCount).padStart(2, '0')}</h3>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-[#79161e]/5 rounded-xl text-[#79161e]">
                  <span className="material-symbols-outlined">pending_actions</span>
                </div>
                {pendingDocsCount > 0 && (
                  <span className="text-xs font-bold text-[#79161e] bg-[#ffdad8] px-2 py-1 rounded-full">Urgent</span>
                )}
              </div>
              <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Documents Pending</p>
              <h3 className="text-3xl font-bold text-[#0f172a]">{String(pendingDocsCount).padStart(2, '0')}</h3>
            </div>

            <div
              className="p-6 rounded-2xl text-white shadow-xl flex flex-col justify-between"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-sm">auto_awesome</span>
                  <span className="text-xs font-semibold">NEXT STEP</span>
                </div>
                <h3 className="text-lg font-bold mb-2">
                  {inProgress ? stageLabel(inProgress.currentStage) : 'No active steps'}
                </h3>
                <p className="text-sm opacity-90">
                  {inProgress ? `Your ${inProgress.opportunity?.title || 'application'} is progressing.` : 'Start a new application to begin your journey.'}
                </p>
              </div>
              {inProgress && (
                <Link to={`/portal/applications/${inProgress.id}`} className="mt-4 bg-white text-[#00255a] py-2 px-6 rounded-lg font-bold text-sm self-start">
                  View Details
                </Link>
              )}
            </div>
          </div>

          <section className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#0f172a]">Current Applications</h3>
              <Link to="/portal/applications" className="text-[#00255a] font-semibold text-sm hover:underline">View All</Link>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {applications.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-12 text-center text-[#64748b]">
                  No applications yet. <Link to="/openings" className="text-[#00255a] font-semibold">Browse opportunities</Link> to get started.
                </div>
              ) : (
                applications.map((app) => {
                  const progress = stageProgress(app.currentStage);
                  return (
                    <div key={app.id} className="bg-white rounded-2xl border border-[#e2e8f0] p-6 hover:shadow-lg transition-all flex flex-col lg:flex-row lg:items-center gap-8">
                      <div className="flex items-center gap-4 lg:w-1/4">
                        <div className="w-16 h-16 rounded-xl bg-[#eceef0] flex items-center justify-center">
                          <span className="material-symbols-outlined text-[#00255a] text-2xl">
                            {app.type === 'STUDY' ? 'school' : 'work'}
                          </span>
                        </div>
                        <div>
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-[#d8e2ff] text-[#001a42] mb-1 uppercase">
                            {app.type}
                          </span>
                          <h4 className="font-bold text-[#0f172a]">{app.opportunity?.title || 'General Application'}</h4>
                          <p className="text-xs text-[#64748b]">{app.opportunity?.country}</p>
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between mb-2">
                          <span className="text-xs font-bold text-[#00255a] uppercase">{stageLabel(app.currentStage)}</span>
                          <span className="text-xs font-bold text-[#00255a]">{progress}%</span>
                        </div>
                        <div className="h-2 w-full bg-[#eceef0] rounded-full overflow-hidden">
                          <div
                            className="h-full transition-all"
                            style={{ width: `${progress}%`, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
                          />
                        </div>
                      </div>
                      <div className="lg:w-1/6 flex justify-end">
                        <Link
                          to={`/portal/applications/${app.id}`}
                          className="w-full lg:w-auto text-center px-6 py-3 border-2 border-[#00255a] text-[#00255a] rounded-xl font-bold text-sm hover:bg-[#00255a] hover:text-white transition-all"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-sm">
              <h3 className="text-lg font-bold text-[#0f172a] mb-4">Pending Documents</h3>
              <div className="space-y-4">
                {documents.length === 0 ? (
                  <p className="text-[#64748b] text-sm">No pending document requests.</p>
                ) : (
                  documents.map((doc) => (
                    <Link
                      key={doc.id}
                      to={`/portal/applications/${doc.applicationId}/documents`}
                      className="flex items-center justify-between p-4 bg-[#f2f4f6] rounded-xl hover:border hover:border-[#ff8483] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-[#79161e]">upload_file</span>
                        <div>
                          <p className="text-sm font-semibold text-[#0f172a]">{doc.documentType}</p>
                          <p className="text-[10px] text-[#64748b]">Required for {doc.applicationTitle}</p>
                        </div>
                      </div>
                      <span className="material-symbols-outlined text-[#44474f]">chevron_right</span>
                    </Link>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-sm">
              <h3 className="text-lg font-bold text-[#0f172a] mb-4">Recent Feedback</h3>
              <p className="text-[#64748b] text-sm">Agent messages will appear here once your application is under review.</p>
            </div>
          </div>
        </div>

        <a
          href="https://wa.me/263775827371"
          className="fixed bottom-8 right-8 w-14 h-14 bg-[#00255a] text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform z-50 group"
        >
          <span className="material-symbols-outlined text-2xl">chat</span>
          <span className="absolute right-full mr-4 bg-[#00255a] text-white px-3 py-1 rounded-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            Chat with Advisor
          </span>
        </a>
      </main>
    </div>
  );
}
